import type { ExportJob } from '@complan/contracts';
import { isTerminalExportState } from './export-centre-data';

/**
 * The pure presentation model for one export row's job status (task 23.5).
 *
 * A row is either idle (no job yet), running (queued/running — show progress,
 * R18.4), succeeded (offer the download, R14.11), or failed/timed-out (state the
 * no-file-produced / data-unchanged outcome, R14.11/R15.9/R18.17). Keeping this a
 * pure function of the job (plus the "start failed" flag the mutation raises)
 * makes the terminal-state handling directly unit-testable without a DOM.
 */

/** How a row should present its current export attempt. */
export type ExportRowStatus =
  | { readonly kind: 'idle' }
  | { readonly kind: 'running'; readonly progressPercent: number }
  | { readonly kind: 'succeeded'; readonly initiativeCount: number | null }
  | { readonly kind: 'failed' }
  | { readonly kind: 'timed_out' }
  | { readonly kind: 'start_failed' };

export interface ExportRowInputs {
  /** The polled job, or `null` before one has been requested. */
  readonly job: ExportJob | null;
  /** True while the start POST is in flight. */
  readonly isStarting: boolean;
  /** True when the start POST itself failed (network/validation) — no job exists. */
  readonly startFailed: boolean;
}

/**
 * Derives the row status from the job and the mutation flags.
 *
 * Precedence: a failed *start* (no job at all) is reported first; then a job's
 * terminal state decides succeeded / failed / timed_out; a non-terminal job, or a
 * start still in flight, is `running`; absent both, the row is idle.
 */
export function deriveExportRowStatus(inputs: ExportRowInputs): ExportRowStatus {
  const { job, isStarting, startFailed } = inputs;

  if (startFailed && job === null) {
    return { kind: 'start_failed' };
  }

  if (job !== null && isTerminalExportState(job.state)) {
    if (job.state === 'succeeded') {
      return { kind: 'succeeded', initiativeCount: job.initiativeCount };
    }
    if (job.state === 'timed_out') {
      return { kind: 'timed_out' };
    }
    return { kind: 'failed' };
  }

  if (isStarting || job !== null) {
    return { kind: 'running', progressPercent: job?.progressPercent ?? 0 };
  }

  return { kind: 'idle' };
}

/**
 * Whether a succeeded job carries a usable download token. `succeeded` is the only
 * state that ever does: a failed or timed-out job has nothing to download and says
 * so through `state` alone (contracts `exportJobSchema`), so a token is never
 * shown for a no-file outcome.
 */
export function hasDownloadableFile(job: ExportJob | null): job is ExportJob & {
  readonly downloadToken: string;
} {
  return job !== null && job.state === 'succeeded' && typeof job.downloadToken === 'string';
}
